import { useState, useEffect, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import useImage from '../../hooks/useImage';

const CameraCanvas = ({ overlay, size }) => {
    const [videoStream, setVideoStream] = useState(null);
    const [videoElement, setVideoElement] = useState(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [error, setError] = useState(null);
    const containerRef = useRef(null);
    const konvaVideoLayerRef = useRef(null);
    const konvaOverlayLayerRef = useRef(null);
    const animationRef = useRef(null);
    const overlayImage = useImage(overlay);

    // Access camera stream
    useEffect(() => {
        let video;
        const initCamera = async () => {
            try {
                // Get list of media devices
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');

                // Find the back camera
                let selectedCamera = videoDevices.find(device => device.label.toLowerCase().includes('back')) ||
                    videoDevices.find(device => device.label.toLowerCase().includes('rear')) ||
                    videoDevices[0]; // Fallback to the first available camera

                const constraints = selectedCamera.deviceId ?
                    { video: { deviceId: { exact: selectedCamera.deviceId } } } :
                    { video: true }; // Fallback to default camera if deviceId is empty

                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                video = document.createElement('video');
                video.srcObject = stream;
                video.autoplay = true;
                video.playsInline = true;
                video.onloadedmetadata = () => {
                    setVideoElement(video);
                    setVideoStream(stream);
                };
                video.onloadeddata = () => {
                };
                video.onerror = (error) => {
                    setError('Video element error');
                };
            } catch (error) {
                if (error.name === 'NotAllowedError') {
                    setError('دسترسی به دوربین ممنوع شد. لطفاً اجازه دوربین را بدهید.');
                } else if (error.name === 'NotFoundError') {
                    setError('دستگاه دوربینی پیدا نشد. لطفا یک دوربین وصل کنید');
                } else if (error.name === 'NotReadableError') {
                    setError('دوربین در حال حاضر توسط برنامه دیگری در حال استفاده است.');
                    initCamera()
                } else if (error.name === 'OverconstrainedError') {
                    setError('هیچ دوربینی مطابق با محدودیت ها یافت نشد.');
                } else {
                    setError('Error accessing camera: ' + error.message);
                }
            }
        };

        // Request permissions explicitly
        navigator.permissions.query({ name: 'camera' }).then(permissionStatus => {
            if (permissionStatus.state === 'granted') {
                initCamera();
            } else if (permissionStatus.state === 'prompt') {
                navigator.mediaDevices.getUserMedia({ video: true })
                    .then(() => {
                        initCamera();
                    })
                    .catch(error => {
                        setError('Error accessing camera: ' + error.message);
                    });
            } else {
                setError('دسترسی به دوربین ممنوع شد. لطفاً اجازه دوربین را بدهید.');
            }
        });

        // Clean up video stream on component unmount
        return () => {
            if (videoStream) {
                videoStream.getTracks().forEach(track => track.stop());
            }
            if (video) {
                video.pause();
                video.srcObject = null;
            }
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    // Handle container resizing
    useEffect(() => {
        const updateDimensions = () => {
            if (containerRef.current) {
                setDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight,
                });
            }
        };

        // Initial dimensions
        updateDimensions();

        // Update dimensions on window resize
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []);

    // Draw video frame onto Konva canvas
    useEffect(() => {
        const drawFrame = () => {
            if (videoElement && konvaVideoLayerRef.current) {
                const canvas = konvaVideoLayerRef.current.getCanvas()._canvas;
                const context = canvas.getContext('2d');
    
                if (videoElement.readyState >= videoElement.HAVE_CURRENT_DATA) {
                    const videoWidth = videoElement.videoWidth;
                    const videoHeight = videoElement.videoHeight;
                    const canvasWidth = dimensions.width;
                    const canvasHeight = dimensions.height;
    
                    // Calculate aspect ratio and adjust dimensions
                    const videoAspectRatio = videoWidth / videoHeight;
                    const canvasAspectRatio = canvasWidth / canvasHeight;
    
                    let drawWidth, drawHeight, offsetX, offsetY;
    
                    if (videoAspectRatio > canvasAspectRatio) {
                        // Video is wider than canvas
                        drawWidth = canvasHeight * videoAspectRatio;
                        drawHeight = canvasHeight;
                        offsetX = (canvasWidth - drawWidth) / 2;
                        offsetY = 0;
                    } else {
                        // Video is taller than canvas
                        drawWidth = canvasWidth;
                        drawHeight = canvasWidth / videoAspectRatio;
                        offsetX = 0;
                        offsetY = (canvasHeight - drawHeight) / 2;
                    }
    
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(
                        videoElement,
                        offsetX, offsetY, // Position
                        drawWidth, drawHeight // Scaled dimensions
                    );
                    konvaVideoLayerRef.current.batchDraw();
                }

                animationRef.current = requestAnimationFrame(drawFrame);
            }
        };

        drawFrame();

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [videoElement, dimensions]);

    // Force an update of the Konva overlay layer once the overlay image is loaded
    useEffect(() => {
        if (overlayImage && konvaOverlayLayerRef.current) {
            konvaOverlayLayerRef.current.batchDraw();
        }
    }, [overlayImage]);

    return (
        <div ref={containerRef} className="relative w-full h-full">
            {error && (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-red-600 bg-opacity-75 text-white text-lg p-4">
                    {error}
                </div>
            )}
            <Stage width={dimensions.width} height={dimensions.height}>
                {/* Video Layer */}
                <Layer ref={konvaVideoLayerRef}>
                    {/* <KonvaImage
                        width={dimensions.width}
                        height={dimensions.height}
                    /> */}
                </Layer>
                {/* Overlay Image Layer */}
                <Layer ref={konvaOverlayLayerRef}>
                    {overlayImage && (
                        <KonvaImage
                            image={overlayImage}
                            x={250}
                            y={100}
                            draggable
                            width={(size.width * 2.9)}
                            height={(size.height * 2.9)}
                            shadowColor="rgba(0, 0, 0, 0.5)"
                            shadowBlur={3}
                            shadowOffsetX={1}
                            shadowOffsetY={1}
                        />
                    )}
                </Layer>
            </Stage>
        </div>
    );
};

export default CameraCanvas;
